import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
  useColorModeValue,
  Badge,
  Divider,
  useBreakpointValue,
  Center,
  Icon,
  Link
} from '@chakra-ui/react';
import { 
  CheckCircleIcon, 
  CalendarIcon, 
  TimeIcon, 
  EmailIcon, 
  PhoneIcon,
  InfoIcon,
  ExternalLinkIcon 
} from '@chakra-ui/icons';

function BookingConfirmation({ 
  student, 
  appointmentDetails, 
  onStartOver 
}) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const successColor = useColorModeValue('green.500', 'green.300');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Formatear fecha y hora para mostrar
  const formatDateTime = (date, time) => {
    const dateObj = new Date(`${date}T${time}`);
    return {
      date: dateObj.toLocaleDateString('es-CL', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      time: dateObj.toLocaleTimeString('es-CL', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    };
  };

  const formatted = formatDateTime(
    appointmentDetails.slot.date,
    appointmentDetails.slot.start_time
  );

  // Generar texto para compartir
  const generateShareText = () => {
    return `Cita agendada para ${student.display_name}
📅 Fecha: ${formatted.date}
⏰ Hora: ${formatted.time}
👨‍🏫 Profesor: ${appointmentDetails.slot.teacher_name}
📧 Confirmación enviada a: ${appointmentDetails.guardian.email}`;
  };

  // Generar evento de calendario
  const generateCalendarEvent = () => {
    const startDate = new Date(`${appointmentDetails.slot.date}T${appointmentDetails.slot.start_time}`);
    const endDate = new Date(`${appointmentDetails.slot.date}T${appointmentDetails.slot.end_time}`);
    
    const eventDetails = {
      title: `Reunión - ${appointmentDetails.slot.teacher_name}`,
      start: startDate.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, ''),
      end: endDate.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, ''),
      description: `Reunión con ${appointmentDetails.slot.teacher_name} para ${student.display_name}`
    };

    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventDetails.title)}&dates=${eventDetails.start}/${eventDetails.end}&details=${encodeURIComponent(eventDetails.description)}`;
    
    return googleCalendarUrl;
  };

  return (
    <VStack spacing={6} align="stretch" maxW="600px" mx="auto">
      {/* Encabezado de confirmación */}
      <Center>
        <VStack spacing={4}>
          <Icon as={CheckCircleIcon} w={16} h={16} color={successColor} />
          <VStack spacing={2} textAlign="center">
            <Text 
              fontSize={isMobile ? 'xl' : '2xl'} 
              fontWeight="bold" 
              color={successColor}
            >
              ¡Cita Confirmada!
            </Text>
            <Text 
              fontSize={isMobile ? 'sm' : 'md'} 
              color="gray.600"
            >
              Su reserva ha sido procesada exitosamente
            </Text>
          </VStack>
        </VStack>
      </Center>

      {/* Detalles de la cita */}
      <Box border="1px solid" borderColor={borderColor} borderRadius="md" bg={cardBg}>
        <Box p={4}>
          <VStack spacing={4} align="stretch">
            <Text fontWeight="bold" fontSize="lg" color={textColor}>
              Detalles de la Cita
            </Text>
            
            <VStack spacing={3} align="start">
              {/* Estudiante */}
              <HStack spacing={3}>
                <Icon as={InfoIcon} color="blue.500" />
                <VStack spacing={0} align="start">
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Estudiante:
                  </Text>
                  <Text fontSize="md" fontWeight="bold">
                    {student.display_name}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    RUT: {student.formatted_rut || student.username}
                  </Text>
                </VStack>
              </HStack>

              <Divider />

              {/* Fecha y hora */}
              <HStack spacing={3}>
                <Icon as={CalendarIcon} color="blue.500" />
                <VStack spacing={0} align="start">
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Fecha:
                  </Text>
                  <Text fontSize="md" fontWeight="bold" textTransform="capitalize">
                    {formatted.date}
                  </Text>
                </VStack>
              </HStack>

              <HStack spacing={3}>
                <Icon as={TimeIcon} color="blue.500" />
                <VStack spacing={0} align="start">
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Hora:
                  </Text>
                  <Text fontSize="md" fontWeight="bold">
                    {formatted.time}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Duración: 30 minutos
                  </Text>
                </VStack>
              </HStack>

              <Divider />

              {/* Profesor */}
              <HStack spacing={3}>
                <Icon as={InfoIcon} color="blue.500" />
                <VStack spacing={0} align="start">
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Profesor:
                  </Text>
                  <Text fontSize="md" fontWeight="bold">
                    {appointmentDetails.slot.teacher_name}
                  </Text>
                </VStack>
              </HStack>

              <Divider />

              {/* Datos del apoderado */}
              <VStack spacing={2} align="start" w="full">
                <Text fontSize="sm" fontWeight="medium" color="gray.600">
                  Datos del apoderado:
                </Text>
                
                <HStack spacing={3}>
                  <Icon as={InfoIcon} color="gray.400" />
                  <Text fontSize="sm">
                    <strong>Nombre:</strong> {appointmentDetails.guardian.name}
                  </Text>
                </HStack>
                
                <HStack spacing={3}>
                  <Icon as={EmailIcon} color="gray.400" />
                  <Text fontSize="sm">
                    <strong>Email:</strong> {appointmentDetails.guardian.email}
                  </Text>
                </HStack>
                
                {appointmentDetails.guardian.phone && (
                  <HStack spacing={3}>
                    <Icon as={PhoneIcon} color="gray.400" />
                    <Text fontSize="sm">
                      <strong>Teléfono:</strong> {appointmentDetails.guardian.phone}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </VStack>
          </VStack>
        </Box>
      </Box>

      {/* Número de confirmación */}
      {appointmentDetails.confirmation_code && (
        <Alert status="success" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertDescription>
              <VStack spacing={1} align="start">
                <Text fontSize="sm" fontWeight="bold">
                  Código de Confirmación:
                </Text>
                <Badge colorScheme="green" fontSize="sm" px={2} py={1}>
                  {appointmentDetails.confirmation_code}
                </Badge>
                <Text fontSize="xs" color="gray.600">
                  Guarde este código para futuras referencias
                </Text>
              </VStack>
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Acciones */}
      <VStack spacing={3}>
        <Text fontWeight="bold" fontSize="md" color={textColor}>
          Próximos Pasos
        </Text>

        <VStack spacing={2} w="full">
          {/* Agregar al calendario */}
          <Button
            as={Link}
            href={generateCalendarEvent()}
            target="_blank"
            rel="noopener noreferrer"
            colorScheme="blue"
            variant="outline"
            size={isMobile ? 'md' : 'lg'}
            w="full"
            leftIcon={<CalendarIcon />}
            rightIcon={<ExternalLinkIcon />}
            _hover={{ textDecoration: 'none' }}
          >
            Agregar a Google Calendar
          </Button>

          {/* Nueva reserva */}
          <Button
            onClick={onStartOver}
            variant="outline"
            size={isMobile ? 'md' : 'lg'}
            w="full"
          >
            Reservar Otra Cita
          </Button>
        </VStack>
      </VStack>

      {/* Información importante */}
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Box>
          <AlertDescription fontSize="sm">
            <VStack spacing={2} align="start">
              <Text fontWeight="bold">Importante:</Text>
              <VStack spacing={1} align="start" fontSize="xs">
                <Text>• Se ha enviado una confirmación por email</Text>
                <Text>• Llegue 5 minutos antes de la hora agendada</Text>
                <Text>• Para cancelar o reprogramar, contacte directamente al establecimiento</Text>
                <Text>• Si no puede asistir, por favor avise con al menos 24 horas de anticipación</Text>
              </VStack>
            </VStack>
          </AlertDescription>
        </Box>
      </Alert>

      {/* Footer */}
      <Box border="1px solid" borderColor={borderColor} borderRadius="md">
        <Box px={4} py={3}>
          <Text fontSize="xs" color="gray.500" textAlign="center">
            Gracias por utilizar nuestro sistema de reservas online.
            Su cita ha sido confirmada y registrada en el sistema.
          </Text>
        </Box>
      </Box>
    </VStack>
  );
}

export default BookingConfirmation;